import { pages } from '@/services/Api'
import jwt from 'jsonwebtoken'

class JwtManager {
  constructor() {
    this._refresh = 10
    this._token = false
    this._timeout = false
    this._payload = {}
    this._key = 'pages_auth'
    this._events = {
      async missing() {},
      async payload() {},
      async expired() {},
      async error() {},
    }
  }

  get token() {
    return this._token
  }

  set token(value) {
    this._token = value
    this.load()
  }

  get payload() {
    return this._payload
  }

  async initialize(handlers) {
    this._events = handlers

    // Detect changes in jwt from other windows
    window.addEventListener('storage', ev => {
      if (ev.key !== this._key) {
        return
      }
      this.update(ev.newValue)
    })

    return await this.load()
  }

  async refresh() {
    try {
      const response = await pages.post(
        '/graphql',
        {
          query: `
          mutation refreshToken {
            refreshToken
          }
        `,
        },
        {
          headers: {
            Authorization: this.token,
          },
        }
      )

      const { data, errors } = response.data
      if (errors && errors.length) {
        await this._events.error(errors)
        return
      }

      this.update(data.refreshToken)
    } catch (err) {
      await this._events.error(err)
    }
  }

  clear() {
    localStorage.removeItem(this._key)
  }

  async load() {
    if (this._timeout !== false) {
      clearTimeout(this._timeout)
    }

    const token = localStorage.getItem(this._key) || false

    if (!token) {
      await this._events.missing()
      this._token = false
      return false
    }

    this._token = token

    const payload = jwt.decode(token, { complete: false })
    if (!payload) {
      localStorage.removeItem(this._key)
      await this._events.missing()
      return false
    }

    this._payload = payload

    if (payload.exp) {
      const now = Date.now()
      const expiration = new Date(payload.exp * 1000)

      if (expiration <= now) {
        await this.clear()
        await this._events.expired(payload)
        return false
      }

      const remaining = Math.floor((expiration.valueOf() - now.valueOf()) / 1000)

      if (remaining - this._refresh < 0) {
        await this.refresh()
      } else {
        const timeout = (remaining - this._refresh) * 1000
        this.schedule(timeout)
      }
    }

    await this._events.payload(payload)
    return true
  }

  update(token) {
    localStorage.setItem(this._key, token)
    this._token = token
    this.load()
  }

  schedule(time) {
    if (this._timeout !== false) {
      clearTimeout(this._timeout)
    }
    this._timeout = setTimeout(this.refresh.bind(this), time)
  }

  isAdmin() {
    return this._payload.user.role === 'ADVANCED'
  }
}

export default new JwtManager()
